import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Brand } from '../components/Brand';
import { useAuth } from '../api/auth';
import { ApiError } from '../api/client';
import { Button, FormField, TextInput } from '../components/DesignSystem';

interface LocationState {
  from?: string;
}

/** Email + password sign-in against POST /auth/login. */
export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (user !== null) {
    return <Navigate replace to="/dashboard" />;
  }

  function validate(): boolean {
    const nextErrors: { email?: string; password?: string } = {};

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = 'Enter a valid email address';
    }

    if (password.length < 8) {
      nextErrors.password = 'Password must be at least 8 characters';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await login(email, password);
      const state = location.state as LocationState | null;
      navigate(state?.from ?? '/dashboard', { replace: true });
    } catch (error) {
      const message =
        error instanceof ApiError && error.status === 401
          ? 'Email or password is incorrect'
          : error instanceof ApiError
            ? error.message
            : 'Unable to reach the server — please try again';
      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="App rfp-login">
      <section className="rfp-login__card">
        <Brand />
        <h1>Welcome back</h1>
        <p className="rfp-login__intro">Sign in to continue to your dashboard.</p>

        <form className="rfp-login__form" noValidate onSubmit={handleSubmit}>
          {formError && (
            <p className="rfp-login__error" role="alert">
              {formError}
            </p>
          )}

          <FormField error={fieldErrors.email} htmlFor="login-email" label="Email">
            <TextInput
              autoComplete="email"
              id="login-email"
              invalid={fieldErrors.email !== undefined}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@studio.com"
              type="email"
              value={email}
            />
          </FormField>

          <FormField error={fieldErrors.password} htmlFor="login-password" label="Password">
            <TextInput
              autoComplete="current-password"
              id="login-password"
              invalid={fieldErrors.password !== undefined}
              onChange={(event) => setPassword(event.target.value)}
              type="password"
              value={password}
            />
          </FormField>

          <Link className="wf-password-help" to="/reset-password">
            Forgotten password?
          </Link>

          <Button disabled={isSubmitting} type="submit">
            {isSubmitting ? 'Signing in…' : 'Sign In'} <span aria-hidden="true">→</span>
          </Button>
        </form>
        <div className="wf-auth-links">
          <Link to="/request-access">Don’t have an account? Create account</Link>
          <Link to="/">Continue as guest</Link>
        </div>
      </section>
    </main>
  );
}
